import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavMenuType } from '@Components/NavBar/navbar-constants';
import { BpmNavigateInput } from '../utils/routing/location-constants';
import { AppDispatch, RootState } from '.';
import { makeReducer } from './utils';

const ROOT = 'navbar';

const SET_OPEN_TOP_MENU = `${ROOT}/SET_OPEN_TOP_MENU`;
const SET_OPEN_SUB_MENU = `${ROOT}/SET_OPEN_SUB_MENU`;
const SET_OPEN_TERTIARY_MENU = `${ROOT}/SET_OPEN_TERTIARY_MENU`;
const SET_QUEUED_NAVIGATION = `${ROOT}/SET_QUEUED_NAVIGATION`;

interface NavBarReduxState {
  openTopMenu?: NavMenuType;
  openSubMenu?: string;
  openTertiaryMenu?: string;
  queuedNavigation?: BpmNavigateInput;
}

export const useNavBarState = () => {
  const dispatch = useDispatch<AppDispatch>();
  const openTopMenu = useSelector((state: RootState) => state.navbar.openTopMenu);
  const openSubMenu = useSelector((state: RootState) => state.navbar.openSubMenu);
  const openTertiaryMenu = useSelector((state: RootState) => state.navbar.openTertiaryMenu);

  const setOpenTopMenu = useCallback(
    (menu: NavMenuType | undefined) => dispatch({ type: SET_OPEN_TOP_MENU, menu }),
    [dispatch]
  );
  const setOpenSubMenu = useCallback(
    (menu: string | undefined) => dispatch({ type: SET_OPEN_SUB_MENU, menu }),
    [dispatch]
  );
  const setOpenTertiaryMenu = useCallback(
    (menu: string | undefined) => dispatch({ type: SET_OPEN_TERTIARY_MENU, menu }),
    [dispatch]
  );

  const queuedNavigation = useSelector((state: RootState) => state.navbar.queuedNavigation);
  const setQueuedNavigation = useCallback(
    (navigation: BpmNavigateInput | undefined) =>
      dispatch({ type: SET_QUEUED_NAVIGATION, navigation }),
    [dispatch]
  );

  return {
    openTopMenu,
    openSubMenu,
    openTertiaryMenu,
    setOpenTopMenu,
    setOpenSubMenu,
    setOpenTertiaryMenu,
    queuedNavigation,
    setQueuedNavigation
  };
};

const setOpenTopMenuReducer = (
  state: NavBarReduxState,
  { menu }: { menu: NavMenuType | undefined }
): NavBarReduxState => ({ ...state, openTopMenu: menu });

const setOpenSubMenuReducer = (
  state: NavBarReduxState,
  { menu }: { menu: string | undefined }
): NavBarReduxState => ({ ...state, openSubMenu: menu });

const setOpenTertiaryMenuReducer = (
  state: NavBarReduxState,
  { menu }: { menu: string | undefined }
): NavBarReduxState => ({ ...state, openTertiaryMenu: menu });

const setQueuedNavigationReducer = (
  state: NavBarReduxState,
  { navigation }: { navigation: BpmNavigateInput | undefined }
): NavBarReduxState => ({ ...state, queuedNavigation: navigation });

export default makeReducer<NavBarReduxState>(
  {},
  {
    [SET_OPEN_TOP_MENU]: setOpenTopMenuReducer,
    [SET_OPEN_SUB_MENU]: setOpenSubMenuReducer,
    [SET_OPEN_TERTIARY_MENU]: setOpenTertiaryMenuReducer,
    [SET_QUEUED_NAVIGATION]: setQueuedNavigationReducer
  }
);
